import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { debounceTime, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-select-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    TranslateModule,
    SearchBarComponent,
    MatIconModule,
    MatFormFieldModule,
    ButtonComponent,
    SpinnerComponent,
    NoDataComponent
  ],
  templateUrl: './select-user.component.html',
  styleUrls: ['./select-user.component.scss']
})

export class SelectUserComponent implements AfterViewInit, OnDestroy {
  loading: boolean = false;
  @ViewChild('userPaginator') userPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  users: User[] = [];
  selectedUsers: User[] = [];
  usersCount: number = 0;
  usersData: MatTableDataSource<User> = new MatTableDataSource();
  usersColumns: string[] = ['user', 'project', 'actions'];
  searchText: string = '';
  project: Project = defaultProject;
  private readonly destroy$ = new Subject<void>();
  requestCancel$: Subject<void> = new Subject<void>();
  searchSubject$: Subject<string> = new Subject<string>();
  lastEmptySearch: string = "";

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { project: Project },
    private userService: UserService,
    private dialogRef: MatDialogRef<SelectUserComponent>,
    private projectService: ProjectService,
    private router: Router,
    public dialog: MatDialog,
    private pop: Pop
  ) {
    this.project = data.project;
  }

  ngAfterViewInit(): void {
    this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(text => {
      if (text !== this.searchText) {
        if (this.lastEmptySearch && text.startsWith(this.lastEmptySearch)) return;
        this.searchText = text;
        this.userPaginator.pageIndex = 0;
        this.loadUsers();
      }
    });

    Promise.resolve().then(() => {
      this.loadUsers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    this.searchSubject$.next(text);
  }

  loadUsers(): void {
    this.requestCancel$.next();
    this.loading = true;

    const query: any = {
      offset: this.userPaginator ? this.userPaginator.pageIndex * this.userPaginator.pageSize : 0,
      limit: this.userPaginator ? this.userPaginator.pageSize : 10,
    };

    const searchText = this.searchText && this.searchText.trim() !== '' ? this.searchText.trim() : "";

    this.userService.searchAndCountUsers(searchText, query)
      .pipe(takeUntil(this.requestCancel$))
      .subscribe({
        next: (res: { results: User[], total: number }) => {
          this.lastEmptySearch = (res.total === 0 && searchText) ? searchText : "";
          this.users = res.results.map(u => ({
            ...u,
            selected: u.projects?.some((p: string | Project) => {
              const projectId = typeof p === 'string' ? p : (p.id ?? p._id);
              return projectId === this.project.id;
            }) ?? false
          }));

          this.users = this.users.sort((a, b) =>
            a.email.localeCompare(b.email)
          );

          this.usersData.data = this.users;
          this.usersCount = res.total;

          if (this.userPaginator)
            this.userPaginator.length = res.total;

          this.usersData.sort = this.sort;
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
        }
      });
  }

  editUser(userId: string): void {
    this.dialogRef.close();
    this.router.navigate([`users/edit-user`], { queryParams: { userId } });
  }

  onUserSelected(event: MatCheckboxChange, user: User): void {
    try {

      if (!user.projects) user.projects = [];

      const userProjectIndex = user.projects.findIndex((p: string | Project) => {
        const projectId = typeof p === 'string' ? p : (p.id ?? p._id);
        return projectId === this.project.id;
      });

      if (userProjectIndex >= 0 && !event.checked) {
        user.projects.splice(userProjectIndex, 1);
      } else if (userProjectIndex === -1 && event.checked) {
        user.projects.push(this.project);
      }

      const _user = {
        id: user.id,
        projects: user.projects
          .map((p: string | Project) => {
            return typeof p === 'string' ? p : (p.id ?? p._id);
          })
      };

      this.userService.setUser(_user)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.projectService.updateUserAccess(user.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (_res: any) => {
                  this.pop.success("Accès utilisateur mis à jour avec succès")
                  this.loading = false;
                },
                error: (err: Error) => {
                  this.loading = false;
                  this.pop.error("Erreur lors de la mise à jour des accès utilisateur")
                  console.error('Erreur lors de la mise à jour des accès utilisateur:', err);
                }
              });
          },
          error: (err: Error) => {
            console.error('Erreur lors de la mise à jour de l\'utilisateur:', err);
          }
        });
    } catch (e) {
      console.error('Erreur lors de la sélection de l\'utilisateur:', e);
    }
  }

  save(): void {
    this.dialogRef.close();
  }

  handlePageEvent(e: PageEvent) {
    this.loadUsers();
  }
}
